// src/pages/3_RDD.js
import React, { useEffect, useState, useMemo, useContext, useCallback } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { ChevronLeftIcon, CheckCircleIcon, XCircleIcon, XIcon, ExclamationIcon } from '@heroicons/react/solid';
// import { DocumentTextIcon, ChevronLeftIcon, ChevronRightIcon, CheckCircleIcon, XCircleIcon, StopIcon, MicrophoneIcon, XIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import InfoTooltip from '../../../components/InfoTooltip';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth } from '../../../firebase';
import { serverTimestamp } from 'firebase/firestore';
// Use the original clinicianSSI slot (not the Lead-override-aware stage check).
// The RDD page remains the original SSI clinician's record even after a Lead
// takeover (SLaM/3 Borough use Review & Synthesis instead of RDD, but this
// carve-out keeps behaviour consistent for any org that still has RDD).
import { isOriginalSSIClinician } from '../../../utils/clinicianPermissions';
import { InformationCircleIcon } from '@heroicons/react/solid';
import { buildFirstGeneratedFields, buildFirstCompletedFields, appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import { isADHDProvisionalPrimaryDiagnosis, computeCriterionEPrefill } from '../../../utils/conditionChecks';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import SSIResetBanner from '../../../components/SSIResetBanner';

export default function RecommendedDiagnosis() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  const [canEdit, setCanEdit] = useState(false);

  // Add state for ADHD diagnosis tracking
  const [isAdhdAfterCI, setIsAdhdAfterCI] = useState(false);

  // Check if SSI reset is in progress (3 Borough / SLaM workflow)
  const ssiResetInfo = useMemo(() => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    if (!manageReports?.ssiResetRequired) {
      return { isResetRequired: false };
    }
    return {
      isResetRequired: true,
      resetRequestedByName: manageReports.ssiResetRequestedByName || 'Lead clinician',
      resetReason: manageReports.ssiResetReason || 'Conflict with Lead clinician findings',
      conflictType: manageReports.ssiResetConflictType || 'CI',
    };
  }, [patientDataModel]);

  /* 
   * MODULE FLOW AND CRITERION E PREFILL LOGIC
   * ==========================================
   * 
   * This component handles Criterion E: Exclusion of Other Disorders prefill logic
   * differently based on the organisation's module flow:
   * 
   * DEFAULT FLOW (Most providers): CI → SSI → DD
   * - Clinical Interview first: Clinician determines provisional primary diagnosis (isAdhdAfterCI)
   * - Semi-Structured Interview: Criterion E prefilled from CI decision (isAdhdAfterCI)
   * - Diagnostic Decision: Uses SSI recommendation as most recent decision
   * 
   * SLaM FLOW: SSI → CI → DD  
   * - Semi-Structured Interview first: Criterion E prefilled as false (CI hasn't occurred yet)
   * - Clinical Interview: Clinician determines provisional primary diagnosis (isAdhdAfterCI)
   * - Diagnostic Decision: Uses CI decision as most recent, fallback to SSI recommendation
   * 
   * SELECTION INITIALISATION PRIORITY ORDER:
   * 1. Firebase document (saved form data — clinician-entered values)
   * 2. Computed prefill logic (criteria A-E derived from upstream data)
   * 3. null (no data available)
   *
   * On mount the A-D sync effect always applies the latest computed prefills
   * (overriding stale Firestore values), while clinician-only fields like
   * diagnosisDecision are hydrated from Firestore and never auto-modified.
   */

  // Route guard: deferred-DF orgs use the Review & Synthesis page
  // Orgs configured for SSI Review & Synthesis use the /review page
  // instead of RDD. Redirect to /review if they land here via stale bookmark.
  useEffect(() => {
    if (!claims) return;
    if (wf.showSSIReviewPage) {
      navigate(`/patient/${patientId}/proforma/semi-structured-interview/review`, { replace: true });
    }
  }, [claims, wf.showSSIReviewPage, patientId, navigate]);

  // Check if the current clinician is assigned to this stage
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        // Raw clinicianSSI slot — Lead override must not grant RDD edit access.
        const hasEditPermission = isOriginalSSIClinician(patientDataModel, currentUser.uid);
        setCanEdit(hasEditPermission);
      }
    };
    
    checkPermissions();
  }, [patientDataModel]);

  // Check for ADHD diagnosis
  useEffect(() => {
    const useAdhdFlow = isADHDProvisionalPrimaryDiagnosis(patientDataModel);
    console.log('🔍 RDD: Using ADHD Flow:', useAdhdFlow);
    setIsAdhdAfterCI(useAdhdFlow);
  }, [patientDataModel]);

  // Debug state changes
  useEffect(() => {
    console.log('🔍 RDD: State Debug - wf.isSSIFirst:', wf.isSSIFirst, 'wf.showSSIReviewPage:', wf.showSSIReviewPage, 'isAdhdAfterCI:', isAdhdAfterCI);
  }, [wf.isSSIFirst, wf.showSSIReviewPage, isAdhdAfterCI]);

  const currentSectionNumber = 3

  const SECTIONS_SSI = useMemo(() => Object.entries(patientDataModel['dataModel']['3_semiStructuredInterview']['sections']).map(([key, section]) => ({
    key,
    ...section
  })), [patientDataModel]);

  // Get the diagnosis section data
  const diagnosisSection = useMemo(() => {
    return SECTIONS_SSI.find(section => section.sectionNumber === currentSectionNumber) || {};
  }, [SECTIONS_SSI, currentSectionNumber]);

  const questionsSymptoms = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Symptoms"
    );
    if (!section) {
      console.error(`Section with title Symptoms not found.`);
      return [];
    }
    return Object.entries(section.questions || {}).map(([key, question]) => question);
  }, [SECTIONS_SSI]);

  const dataDomains = useMemo(() => {
    const section = SECTIONS_SSI.find(
      section => section.sectionTitle === "Domains of Functional Impairment"
    );
    if (!section) {
      console.error(`Section with title Domains of Functional Impairment not found.`);
      return [];
    }
    return section
  }, [SECTIONS_SSI]);

  const domainImpairment = useMemo(() => {
    if (!dataDomains?.questions) return {
      dsmCriterionD_FunctionalImpairment: null
    };

    const domainValues = Object.values(dataDomains.questions)
      .map(q => q.functioningReduced_Adulthood);

    // If no domain has been explicitly assessed yet (all null/undefined),
    // return null so the criterion stays unselected rather than defaulting to No.
    const hasAnyDecision = domainValues.some(v => v === true || v === false);
    if (!hasAnyDecision) {
      return { dsmCriterionD_FunctionalImpairment: null };
    }

    const adulthoodDomainsCount = domainValues.filter(v => v === true).length;
    return {
      dsmCriterionD_FunctionalImpairment: adulthoodDomainsCount >= 1
    };
  }, [dataDomains]);

  // Structured domain-level impairment data for the summary table.
  // Each domain has a title and adulthood/childhood impairment flags from the
  // Domains of Functional Impairment section completed earlier in the SSI.
  const domainImpairmentData = useMemo(() => {
    if (!dataDomains?.questions) return [];
    return Object.entries(dataDomains.questions)
      .sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10))
      .map(([, question]) => ({
        domain: question.DSM_symptom || question.DSM_symptom_prefix || 'Unknown',
        adulthood: question.functioningReduced_Adulthood,
        childhood: question.functioningReduced_Childhood,
      }));
  }, [dataDomains]);

  // Calculate if criterion C is met (symptoms across settings)
  const criterionCMet = useMemo(() => {
    // Skip if no symptom data
    if (!questionsSymptoms || questionsSymptoms.length === 0) return null;

    // Count symptoms present in each setting
    const settingCounts = {
      'Familial': 0,
      'Social': 0,
      'Occupational': 0,
      'Educational': 0,
      'Community': 0
    };

    // Iterate through all symptoms
    questionsSymptoms.forEach(symptom => {
      // Only count symptoms present in adulthood
      if (symptom.symptomPresent_Adulthood === true) {
        // Check which settings this symptom occurs in
        const presentInSettings = symptom.presentInSettings_Adulthood || [];

        // Increment counter for each setting this symptom is present in
        presentInSettings.forEach(setting => {
          if (setting in settingCounts) {
            settingCounts[setting]++;
          }
        });
      }
    });

    // Count how many settings have at least 3 symptoms
    const settingsWithThreePlusSymptoms = Object.values(settingCounts).filter(count => count >= 3).length;

    // Log for debugging
    // console.log("Setting counts:", settingCounts, "Settings with 3+ symptoms:", settingsWithThreePlusSymptoms);

    // Criterion C is met if at least 2 settings have 3+ symptoms
    return settingsWithThreePlusSymptoms >= 2;
  }, [questionsSymptoms]);

  const attentionDeficitData = questionsSymptoms
    ? questionsSymptoms
      .filter(item => item?.DSM_code && item.DSM_code.startsWith('A1'))
      .map(item => ({
        criterion: item.DSM_code,
        symptom: item.DSM_symptom,
        adulthood: item.symptomPresent_Adulthood,
        childhood: item.symptomPresent_Childhood,
      }))
    : [];

  const hyperactivityImpulsivityData = questionsSymptoms
    ? questionsSymptoms
      .filter(item => item?.DSM_code && item.DSM_code.startsWith('A2'))
      .map(item => ({
        criterion: item.DSM_code,
        symptom: item.DSM_symptom,
        adulthood: item.symptomPresent_Adulthood,
        childhood: item.symptomPresent_Childhood,
      }))
    : [];

  const calculateTotals = (data) => {
    const adulthoodTotal = data.filter(item => item.adulthood).length;
    const childhoodTotal = data.filter(item => item.childhood).length;
    return { adulthoodTotal, childhoodTotal };
  };

  const attentionTotals = calculateTotals(attentionDeficitData);
  const hyperactivityTotals = calculateTotals(hyperactivityImpulsivityData);

  // Criterion E prefill: workflow-config-aware computation that accounts
  // for deferred DF, SSI-first, hierarchical vs flat DF models, and
  // whether ADHD is among the conditions in consideration.
  const criterionEPrefill = useMemo(
    () => computeCriterionEPrefill(wf, patientDataModel),
    [wf, patientDataModel]
  );

  const questionPanels = useMemo(() => [
    {
      title: "Criterion A: Symptom Threshold",
      questions: [
        {
          name: 'Are ≥5 inattention symptoms present in adulthood?',
          prefill: attentionTotals.adulthoodTotal >= 5,
          key: "dsmCriterionA_SymptomThreshold_A",
          isOptional: false,
        },
        {
          name: 'Are ≥5 hyperactivity-impulsivity symptoms present in adulthood?',
          prefill: hyperactivityTotals.adulthoodTotal >= 5,
          key: "dsmCriterionA_SymptomThreshold_HI",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion B: Age of Onset",
      questions: [
        {
          name: 'Were several (≥3) symptoms of inattention and/or hyperactivity-impulsivity present before age 12?',
          prefill: attentionTotals.childhoodTotal + hyperactivityTotals.childhoodTotal >= 3,
          key: "dsmCriterionB_AgeOfOnset",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion C: Symptoms Across Settings",
      questions: [
        {
          name: 'Are several (≥3) symptoms present in each of at least ≥2 settings?',
          prefill: criterionCMet,
          key: "dsmCriterionC_SymptomsAcrossSettings",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion D: Functional Impairment",
      questions: [
        {
          name: 'Do the symptoms cause at least moderate psychological, social, and/or educational or occupational impairment?',
          prefill: domainImpairment.dsmCriterionD_FunctionalImpairment,
          key: "dsmCriterionD_FunctionalImpairment",
          isOptional: false,
        },
      ],
    },
    {
      title: "Criterion E: Exclusion of Other Disorders",
      questions: [
        {
          name: 'The symptoms cannot be (better) explained by another psychiatric disorder and are not due to the effect of a substance or medication',
          prefill: criterionEPrefill.value,
          key: "dsmCriterionE_NotOtherExplanation",
          isOptional: false,
        },
      ],
      tooltip: "The symptoms do not occur exclusively during the course of schizophrenia or another psychotic disorder and are not better explained by another mental disorder (e.g., mood disorder, anxiety disorder, dissociative disorder, personality disorder, substance intoxication or withdrawal).",
    },
    {
      title: "Recommended Diagnosis",
      questions: [
        {
          id: 'diagnose_adhd',
          name: 'Has the criteria for ADHD Diagnosis Been Met?',
          radio_options: [
            {
              value: 'Fully met'
            },
            {
              value: 'Partially met',
              tooltip: "Significant symptoms and impairment present"
            },
            {
              value: 'Not met',
              tooltip: "Criteria not met for ADHD diagnosis"
            }
          ],
          prefill: null,
          key: "diagnosisDecision",
          isOptional: false,
        },
        {
          id: 'select_adhd_subtype',
          name: 'Recommended Presentation',
          subtitle: "Select the appropriate presentation based on current symptoms",
          radio_options: [
            {
              value: 'Predominantly Inattentive Presentation',
              tooltip: "≥5 inattention symptoms but <5 hyperactive-impulsive symptoms in adulthood"
            },
            {
              value: 'Predominantly Hyperactive-Impulsive Presentation',
              tooltip: "≥5 hyperactive-impulsive symptoms but <5 inattention symptoms in adulthood"
            },
            {
              value: 'Combined Presentation',
              tooltip: "≥5 inattention and ≥5 hyperactive-impulsive symptoms in adulthood"
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisPresentation",
          isOptional: false,
        },
        {
          id: 'select_adhd_severity',
          name: 'Recommended Severity of Symptoms',
          subtitle: "Select the appropriate level of severity based on the extent of impairment in daily life",
          radio_options: [
            {
              value: 'Moderate',
              tooltip: "Symptoms cause noticeable impairment in multiple domains"
            },
            {
              value: 'Severe',
              tooltip: "Symptoms cause significant disruption to daily functioning"
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Fully met',
          key: "diagnosisSeverity",
          isOptional: false,
        },
        {
          id: 'select_partial_adhd_type',
          name: 'Recommended Diagnostic Classification',
          subtitle: "Select the appropriate classification for partial criteria",
          radio_options: [
            {
              value: 'ADHD in Partial Remission',
              tooltip: "Requires prior ADHD diagnosis; symptoms now fall below the threshold but impairment persists in social, academic, or occupational functioning."
            },
            {
              value: 'Other Specified ADHD',
              tooltip: "Symptoms cause significant impairment but do not meet full ADHD criteria. Clinician must specify the reason."
            },
            {
              value: 'Unspecified ADHD',
              tooltip: "Used when insufficient information/time to clarify (e.g., emergency settings)."
            }
          ],
          prefill: null,
          dependence: ['diagnose_adhd'],
          showWhen: (selections) => selections[5][0] === 'Partially met',
          key: "partialDiagnosisClassification",
          isOptional: false,
        },
        {
          id: 'specify_other_adhd_reason',
          name: 'Specify Reason for Other Specified ADHD',
          subtitle: "Select the reason this patient meets Other Specified ADHD criteria",
          radio_options: [
            {
              value: 'Subthreshold symptoms with impairment'
            },
            {
              value: 'Atypical symptom patterns'
            },
            {
              value: 'Late onset symptoms'
            },
            {
              value: 'Symptom overlap with another disorder'
            },
            {
              value: 'Symptoms that cause impairment in one setting'
            },
            {
              value: 'Symptom variability'
            },
            {
              value: 'Other'
            }
          ],
          prefill: null,
          dependence: ['select_partial_adhd_type'],
          showWhen: (selections) => {
            const diagnosisDecision = selections[5][0];
            const partialType = selections[5][3]; // Index of partialDiagnosisClassification
            return diagnosisDecision === 'Partially met' && partialType === 'Other Specified ADHD';
          },
          key: "otherSpecifiedReason",
          isOptional: false,
        },
        {
          id: 'other_reason_text',
          name: 'Specify Other Reason',
          subtitle: "Please provide details for the other reason selected above",
          prefill: null,
          dependence: ['specify_other_adhd_reason'],
          showWhen: (selections) => {
            const diagnosisDecision = selections[5][0];
            const partialType = selections[5][3]; // Index of partialDiagnosisClassification
            const otherReason = selections[5][4]; // Index of otherSpecifiedReason
            return diagnosisDecision === 'Partially met' &&
              partialType === 'Other Specified ADHD' &&
              otherReason === 'Other';
          },
          isTextInput: true,
          key: "otherSpecifiedReasonText",
          isOptional: false,
        },
        {
          id: 'select_supporting_evidence',
          name: 'Supporting Evidence',
          subtitle: "Is the diagnosis supported by collateral information?",
          checklist_options: [
            {
              value: 'Informant reports',
            },
            {
              value: 'Employer report',
            },
            {
              value: 'School/work records',
            },
            {
              value: 'Medical records',
            },
            {
              value: 'None available',
            }
          ],
          prefill: null,
          showWhen: (selections) => selections[5][0] === 'Fully met' || selections[5][0] === 'Partially met',
          key: "supportedByCollateral",
          isOptional: true,
        },
      ],
    },
  ], [attentionTotals, hyperactivityTotals, domainImpairment, criterionCMet, criterionEPrefill]);

  // Initialise selections from Firestore (clinician-entered values) with computed
  // prefills as fallback.  localStorage was previously used here but has been
  // removed — it created a stale shadow state that competed with Firestore and
  // prefills, causing criteria to appear out of sync after upstream edits.
  // The A-D and E sync effects (below) further reconcile on mount.
  const [selections, setSelections] = useState(() => {
    return questionPanels.map((panel, panelIndex) =>
      panel.questions.map((question, questionIndex) => {
        if (question.key && diagnosisSection) {
          // Normalize legacy "Partner/family reports" label for supportedByCollateral
          if (question.key === 'supportedByCollateral') {
            const raw = diagnosisSection[question.key] ?? [];
            return raw.map(v => v === 'Partner/family reports' ? 'Informant reports' : v);
          }

          return diagnosisSection[question.key] ?? question.prefill ?? null;
        }

        return question.prefill ?? null;
      })
    );
  });

  // Keep derived criteria (A-D) in sync with the latest symptom, setting, and
  // domain data.  On every mount lastAppliedPrefills resets to {}, so the
  // effect applies current computed prefills unconditionally — giving "fresh
  // prefill" semantics whenever the clinician returns to this page.
  //
  // Within a session the effect uses "resume tracking": it records the last
  // prefill it applied per key, and only overwrites the selection when the
  // computed prefill itself changes.  This preserves manual overrides the
  // clinician makes on this page until upstream data actually moves the prefill
  // to a new value.
  //
  // null is a valid prefill (means "data insufficient to recommend") and is
  // applied like any other value — this prevents stale Yes/No from persisting
  // when the underlying data no longer supports a recommendation.
  const DERIVED_CRITERIA_KEYS = useMemo(() => new Set([
    'dsmCriterionA_SymptomThreshold_A',
    'dsmCriterionA_SymptomThreshold_HI',
    'dsmCriterionB_AgeOfOnset',
    'dsmCriterionC_SymptomsAcrossSettings',
    'dsmCriterionD_FunctionalImpairment',
  ]), []);

  const lastAppliedPrefills = React.useRef({});

  useEffect(() => {
    if (questionPanels.length === 0) return;

    // Collect which criteria need updating before touching state, so we can
    // bail out without triggering a re-render when nothing changed.
    const updates = [];

    questionPanels.forEach((panel, panelIndex) => {
      panel.questions.forEach((question, questionIndex) => {
        if (!DERIVED_CRITERIA_KEYS.has(question.key)) return;

        const expectedPrefill = question.prefill;
        const lastApplied = lastAppliedPrefills.current[question.key];

        // Only apply when the computed prefill differs from the last value we
        // applied.  On first mount lastApplied is undefined (distinct from null),
        // so every criterion gets the current prefill.  Manual overrides by the
        // clinician don't change lastApplied, so they are preserved until the
        // upstream data produces a genuinely new prefill value.
        if (lastApplied === undefined || lastApplied !== expectedPrefill) {
          updates.push({ panelIndex, questionIndex, value: expectedPrefill });
          lastAppliedPrefills.current[question.key] = expectedPrefill;
        }
      });
    });

    if (updates.length > 0) {
      // Functional update ensures we compose on top of the latest state,
      // not a stale snapshot — critical when the E sync effect also calls
      // setSelections in the same render cycle.
      setSelections(prev => {
        const next = prev.map(panel => [...panel]);
        updates.forEach(({ panelIndex, questionIndex, value }) => {
          next[panelIndex][questionIndex] = value;
        });
        return next;
      });
    }
  }, [questionPanels, DERIVED_CRITERIA_KEYS]);

  // Criterion E sync: same "fresh prefill on mount, resume tracking within
  // session" pattern as A-D.  Criterion E's prefill is derived from the CI
  // Diagnostic Formulation and workflow config (not from SSI symptom/domain
  // data), so it uses a separate ref.  On mount lastAppliedCriterionE is
  // undefined, so the current prefill is applied regardless of any stale
  // Firestore value.  Within the session, manual overrides are preserved
  // until computeCriterionEPrefill produces a genuinely new value.
  const lastAppliedCriterionE = React.useRef(undefined);

  useEffect(() => {
    if (questionPanels.length === 0) return;

    const criterionEPanelIndex = 4;
    const criterionEQuestionIndex = 0;

    if (questionPanels[criterionEPanelIndex]?.questions[criterionEQuestionIndex]?.key !== 'dsmCriterionE_NotOtherExplanation') return;

    const expectedPrefill = criterionEPrefill.value;

    if (lastAppliedCriterionE.current === undefined || lastAppliedCriterionE.current !== expectedPrefill) {
      lastAppliedCriterionE.current = expectedPrefill;
      // Functional update so this composes correctly with the A-D sync
      // effect when both fire in the same render cycle.
      setSelections(prev => {
        const next = prev.map(panel => [...panel]);
        next[criterionEPanelIndex][criterionEQuestionIndex] = expectedPrefill;
        return next;
      });
    }
  }, [criterionEPrefill, questionPanels]);

  // ---------------------------------------------------------------------------
  // Re-prefill detection: compare Firestore-persisted criterion values against
  // current computed prefills.  A criterion is "re-prefilled" if the clinician
  // had previously saved a value (non-null in Firestore) and the current
  // computed prefill differs from it.  Used by the notification banner and the
  // section status reset below.
  // ---------------------------------------------------------------------------

  // Maps each derived criterion key to a short human-readable label so the
  // banner can say "Criteria A and D" instead of listing Firestore field names.
  // Both A-inattention and A-hyperactivity map to 'A' — the Set deduplicates.
  const CRITERION_LABELS = useMemo(() => ({
    dsmCriterionA_SymptomThreshold_A: 'A',
    dsmCriterionA_SymptomThreshold_HI: 'A',
    dsmCriterionB_AgeOfOnset: 'B',
    dsmCriterionC_SymptomsAcrossSettings: 'C',
    dsmCriterionD_FunctionalImpairment: 'D',
  }), []);

  const reprefilledCriteriaLabels = useMemo(() => {
    const changed = new Set();
    questionPanels.forEach((panel) => {
      panel.questions.forEach((question) => {
        if (!DERIVED_CRITERIA_KEYS.has(question.key)) return;
        const savedValue = diagnosisSection?.[question.key];
        const currentPrefill = question.prefill;
        // Only flag as "re-prefilled" if the clinician had a prior saved value
        // and the new computed prefill differs from what was saved.
        if (savedValue !== undefined && savedValue !== null && savedValue !== currentPrefill) {
          changed.add(CRITERION_LABELS[question.key]);
        }
      });
    });
    return [...changed].sort();
  }, [questionPanels, diagnosisSection, DERIVED_CRITERIA_KEYS, CRITERION_LABELS]);

  // Builds a grammatically correct two-part message describing which criteria
  // were re-prefilled.  Returns { summary, cta } or null if nothing changed.
  const buildReprefillMessage = useCallback((labels) => {
    if (labels.length === 0) return null;

    const formatted = labels.length === 1
      ? labels[0]
      : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

    const noun = labels.length === 1 ? 'Criterion' : 'Criteria';
    const verb = labels.length === 1 ? 'has' : 'have';

    return {
      summary: `The selections for ${noun} ${formatted} ${verb} been updated to reflect changes made to symptoms and domains.`,
      cta: 'Please review and update decisions below as appropriate.',
    };
  }, []);

  // When criteria have been re-derived on mount and the section was previously
  // completed, downgrade status to inProgress so downstream consumers (conflict
  // detection, progress bars) don't treat stale data as a finalised recommendation.
  const statusResetDone = React.useRef(false);

  useEffect(() => {
    if (statusResetDone.current) return;
    if (reprefilledCriteriaLabels.length === 0) return;
    if (diagnosisSection?.status !== 'completed') return;

    statusResetDone.current = true;
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        sections: {
          [currentSectionNumber]: {
            status: 'inProgress',
            progress: 50,
          },
        },
      },
    });
  }, [reprefilledCriteriaLabels, diagnosisSection, patientId, updatePatientFirestore, currentSectionNumber]);

  const handleSelection = async (panelIndex, questionIndex, value) => {

    if (!canEdit) return;
    
    const newSelections = [...selections];

    // If this is the diagnosis decision question (index 5, question 0)
    if (panelIndex === 5 && questionIndex === 0) {
      // Update the main selection
      newSelections[panelIndex][questionIndex] = value;

      // Reset dependent selections based on the new value
      if (value === 'Fully met') {
        // Reset presentation type and severity
        newSelections[panelIndex][1] = null; // Reset subtype
        newSelections[panelIndex][2] = null; // Reset severity
        // Clear partial met options
        newSelections[panelIndex][3] = null; // Reset partial ADHD type
        newSelections[panelIndex][4] = null; // Reset other specified reason
        newSelections[panelIndex][5] = null; // Reset other reason text
      }
      else if (value === 'Partially met') {
        // Clear fully met options
        newSelections[panelIndex][1] = null; // Clear subtype
        newSelections[panelIndex][2] = null; // Clear severity
        // Reset partial options
        newSelections[panelIndex][3] = null; // Reset partial ADHD type
      }
      else {
        // Not met - clear all dependent fields
        newSelections[panelIndex][1] = null;
        newSelections[panelIndex][2] = null;
        newSelections[panelIndex][3] = null;
        newSelections[panelIndex][4] = null;
        newSelections[panelIndex][5] = null;
        newSelections[panelIndex][6] = null; // Supporting evidence
      }

      // Update state
      setSelections(newSelections);

      // Update Firestore with changes
      const updateData = {
        diagnosisDecision: value,
        diagnosisPresentation: null,
        diagnosisSeverity: null,
        partialDiagnosisClassification: null,
        otherSpecifiedReason: null,
        otherSpecifiedReasonText: null,
        supportedByCollateral: value === 'Not met' ? null : newSelections[panelIndex][6],
      };

      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: updateData
          },
        },
      });
    }
    // If this is the partial ADHD type question (index 5, question 3)
    else if (panelIndex === 5 && questionIndex === 3) {
      newSelections[panelIndex][questionIndex] = value;

      // If not "Other Specified ADHD", clear other specified reason fields
      if (value !== 'Other Specified ADHD') {
        newSelections[panelIndex][4] = null; // Reset other specified reason
        newSelections[panelIndex][5] = null; // Reset other reason text
      }

      setSelections(newSelections);

      // Update Firestore
      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              partialDiagnosisClassification: value,
              otherSpecifiedReason: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][4],
              otherSpecifiedReasonText: value !== 'Other Specified ADHD' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    // If this is the other specified reason (index 5, question 4)
    else if (panelIndex === 5 && questionIndex === 4) {
      newSelections[panelIndex][questionIndex] = value;

      // If not "Other", clear the other reason text
      if (value !== 'Other') {
        newSelections[panelIndex][5] = null;
      }

      setSelections(newSelections);

      // Update Firestore
      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              otherSpecifiedReason: value,
              otherSpecifiedReasonText: value !== 'Other' ? null : newSelections[panelIndex][5],
            },
          },
        },
      });
    }
    else {
      // Handle other selections normally
      newSelections[panelIndex][questionIndex] = value;
      setSelections(newSelections);

      // Get the corresponding question key from questionPanels
      const question = questionPanels[panelIndex].questions[questionIndex];

      // Update Firestore with the specific field
      await updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              [question.key]: value,
            },
          },
        },
      });
    }

  };

  const isFormValid = useCallback(() => {
    // Early return if any required data is missing
    if (!selections || !questionPanels || !Array.isArray(selections) || !Array.isArray(questionPanels)) {
      console.log('Missing required data for validation:', { selections, questionPanels });
      return false;
    }

    try {
      // Get diagnostic decision        
      const diagnosisDecision = selections[5][0];

      // If no diagnosis decision selected, form is invalid
      if (!diagnosisDecision) return false;

      // Based on diagnosis decision, validate required fields
      if (diagnosisDecision === 'Fully met') {
        // For 'Fully met', need presentation type and severity
        const subtypeSelected = selections[5][1]; // Presentation type
        const severitySelected = selections[5][2]; // Severity
        if (!subtypeSelected || !severitySelected) return false;
      }
      else if (diagnosisDecision === 'Partially met') {
        // For 'Partially met', need partial ADHD type
        const partialTypeSelected = selections[5][3]; // Partial ADHD type
        if (!partialTypeSelected) return false;

        // If 'Other Specified ADHD' selected, need reason
        if (partialTypeSelected === 'Other Specified ADHD') {
          const reasonSelected = selections[5][4]; // Other specified reason
          if (!reasonSelected) return false;

          // If 'Other' reason selected, need text explanation
          if (reasonSelected === 'Other') {
            const otherText = selections[5][5];
            // Check if the text is null, undefined, or just whitespace
            if (!otherText || otherText.trim() === '') return false;
          }
        }
      }

      // If we reach here, form is valid
      return true;
    } catch (error) {
      console.error('Error in form validation:', error);
      return false;
    }
  }, [selections, questionPanels]);

  const [isSaving, setIsSaving] = useState(false);

  const user = auth.currentUser;
  const clinicianId = user?.uid;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const constructDiagnosisSummary = useCallback(() => {
    let output = [];
    const diagnosisDecision = selections[5][0];

    output.push(`• Criteria for ADHD: ${diagnosisDecision || 'Not specified'}`);

    if (diagnosisDecision === 'Fully met') {
      // For fully met criteria, include presentation type and severity
      if (selections[5][1]) output.push(`• Presentation: ${selections[5][1]}`);
      if (selections[5][2]) output.push(`• Severity: ${selections[5][2]}`);
    }
    else if (diagnosisDecision === 'Partially met') {
      // For partially met criteria, include diagnostic classification
      if (selections[5][3]) {
        output.push(`• Classification: ${selections[5][3]}`);

        // If "Other Specified ADHD" was chosen, include the reason
        if (selections[5][3] === 'Other Specified ADHD' && selections[5][4]) {
          const reasonType = selections[5][4];

          if (reasonType === 'Other') {
            // For "Other" reason, display the user-entered text
            const customReasonText = selections[5][5];
            if (customReasonText && customReasonText.trim() !== '') {
              output.push(`• Reason: Other - ${customReasonText}`);
            } else {
              output.push(`• Reason: Other (no details specified)`);
            }
          } else {
            // For standard reason options
            output.push(`• Reason: ${reasonType}`);
          }
        }
      }
    }

    // Add supporting evidence if available for both Fully met and Partially met
    if ((diagnosisDecision === 'Fully met' || diagnosisDecision === 'Partially met') &&
      selections[5][6] &&
      selections[5][6].length > 0) {
      output.push(`• Supporting Evidence: ${selections[5][6].join(', ')}`);
    }

    return output.map((bullet, index) => (
      <div key={index} className="mb-1">
        {bullet}
      </div>
    ));
  }, [selections]);

  const handleGenerateReport = () => {
    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  // Deferred-DF flow: trigger SSI report generation silently in the background
  // and navigate straight to the DF page without showing a progress dialog.
  // Queue all writes before flushing so they're committed in a single
  // transaction, then chain the Cloud Function to the flush Promise.
  const handleProceedToDF = useCallback(() => {
    setIsDialogOpen(false);

    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['3_semiStructuredInterview']);
    updatePatientFirestore(patientId, {
      '3_semiStructuredInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        ...firstCompletedFields,
      },
      '6_manageReports': {
        ssiReportGenerated: true,
        ssiReportGeneratedAt: serverTimestamp(),
        ssiReportGeneratedBy: auth.currentUser.uid,
        ssiReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('ssi', manageReports),
      }
    });
    // Audit entry is a separate direct Firestore write — fire-and-forget
    appendReportAuditEntry(patientId, {
      module: 'ssi',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ssiReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ssi',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    const dataReady = flushPendingUpdates({ noTimeout: true });
    TriggerGenerateDocumentSection(patientId, 'Semi-Structured Interview', 'Recommended Diagnosis', [], dataReady);

    navigate(`/patient/${patientId}/proforma/semi-structured-interview/diagnostic-formulation`);
  }, [patientId, patientDataModel, updatePatientFirestore, navigate, flushPendingUpdates, wf.isAppointmentDatesEnabled]);

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };

  const handleNextPageButtonClick = (route) => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['3_semiStructuredInterview']);

    const updateData = {
      '3_semiStructuredInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        ...firstCompletedFields,
      },
      '6_manageReports': {
        ssiReportGenerated: true,
        ssiReportGeneratedAt: serverTimestamp(),
        ssiReportGeneratedBy: auth.currentUser.uid,
        ssiReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('ssi', manageReports),
      }
    };

    // 3 Borough / SLaM workflow: Clear SSI reset flag if it was set
    const wasSSIResetRequired = manageReports?.ssiResetRequired === true;
    if (wasSSIResetRequired && wf.isSSIFirst) {
      console.log('🔄 Clearing SSI reset flag - Lead clinician completed SSI re-editing');
      updateData['6_manageReports'].ssiResetRequired = false;
      updateData['6_manageReports'].ssiResetCompletedAt = serverTimestamp();
      updateData['6_manageReports'].ssiResetCompletedBy = auth.currentUser.uid;
      updateData['6_manageReports'].ssiResetCompletedByName = auth.currentUser.displayName;
    }

    updatePatientFirestore(patientId, updateData);

    appendReportAuditEntry(patientId, {
      module: 'ssi',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ssiReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ssi',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    navigate(route);
  };


  const handleFinishSSI = useCallback(async () => {
    setIsSaving(true);

    const updates = {};

    // Iterate over the questionPanels and selections to prepare updates
    questionPanels.forEach((panel, panelIndex) => {
      panel.questions.forEach((question, questionIndex) => {
        const value = selections[panelIndex][questionIndex];
        updates[question.key] = value;
      });
    });

    // Queue the update, then flush to await commit before showing dialog
    try {
      updatePatientFirestore(patientId, {
        '3_semiStructuredInterview': {
          sections: {
            [currentSectionNumber]: {
              ...updates,
              status: 'completed',
              progress: 100,
            },
          },
        },
      });

      await flushPendingUpdates();
      await fetchPatientData(patientId);

      setIsSaving(false);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to update Firestore before navigation:", error);
    } finally {
      setIsSaving(false);
    }
  }, [patientId, fetchPatientData, selections, updatePatientFirestore, questionPanels, flushPendingUpdates]);

  // Navigation handler — fire-and-forget.  RDD doesn't trigger its own
  // paragraph generation; auto-flush on route change persists any edits.
  const handleBackToDomains = useCallback(() => {
    flushPendingUpdates();
    navigate(`/patient/${patientId}/proforma/semi-structured-interview/domains`);
  }, [flushPendingUpdates, navigate, patientId]);

  const breadcrumbTuples = [

    ["Patient Overview", `/patient/${patientId}`],
    ["Semi-Structured Interview"],
    ["Recommended Diagnosis"],
  ];

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />

      {/* SSI Reset Banner - shown when Lead clinician is re-editing SSI (3 Borough / SLaM workflow) */}
      <div className="px-8">
        <SSIResetBanner
          isVisible={ssiResetInfo.isResetRequired}
          resetRequestedByName={ssiResetInfo.resetRequestedByName}
          conflictType={ssiResetInfo.conflictType}
        />
      </div>
      <div className="flex-grow p-8">        

        <div className="flex justify-between items-center mb-3">

          {/* Left column: Back button */}
          <div className="w-1/3 flex justify-start">
          <button
            onClick={handleBackToDomains}
            disabled={isSaving}
            className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isSaving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
          >
            <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />
            <span className="font-bold">Back to Domains</span>
          </button>
          </div>

          {/* Centered heading with tooltip */}
          <div className="w-1/3 flex justify-center items-center">
            <h1 className="text-2xl font-bold">Recommended Diagnosis</h1>
            {/* <h1 className="text-2xl font-bold">Diagnostic Decision</h1> */}
            <InfoTooltip
              content="Your recommendation will be shown in the final Diagnostic Decision section"
              label="Information about recommended diagnosis"
              maxWidth="250px"
              className="flex-shrink-0 ml-1"
            />
          </div>

          {/* Right column: Forward button or empty space */}
          <div className="w-1/3 flex justify-end">
            {canEdit && (
              <button
                onClick={() => handleFinishSSI()}
                className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${
                  isFormValid() ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                disabled={!isFormValid()}
              >
                <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isDFDeferred ? "Go to Diagnostic Formulation" : "Finish Semi-Structured Interview")}</span>
                {wf.isDFDeferred
                  ? <ChevronLeftIcon className="w-5 h-5 ml-2 rotate-180" aria-hidden="true" />
                  : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
            )}
          </div>
        </div>

        <div className="flex space-x-4">
          <div className="w-3/5 rounded-2xl space-y-6">
            {/* Criterion A: Symptom summary table
                Column widths use fixed percentages so the adulthood/childhood
                columns align vertically with the domains table below.
                Criterion (10%) + Symptom (50%) = 60%, Adulthood 20%, Childhood 20%. */}
            <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '10%' }} />
                <col style={{ width: '50%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr className="bg-primary text-white">
                  <th className="p-3 text-center">Criterion DSM-5</th>
                  <th className="p-3">Symptom</th>
                  <th className="p-3 text-center">Present during adulthood</th>
                  <th className="p-3 text-center">Present during childhood</th>
                </tr>
              </thead>
              <tbody>
                {attentionDeficitData.map((item, index) => (
                  <tr key={index} className="bg-primary/5 border-b border-primary/10">
                    <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white">
                      {item.adulthood === true
                        ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                        : item.adulthood === false
                          ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                          : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="p-3 text-center bg-white">
                      {item.childhood === true
                        ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                        : item.childhood === false
                          ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                          : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
                <tr className="bg-primary/15 font-bold text-primary">
                  <td className="p-3" colSpan="2">Total criteria: Attention Deficit</td>
                  <td className="p-3 text-center">{attentionTotals.adulthoodTotal} / 9</td>
                  <td className="p-3 text-center">{attentionTotals.childhoodTotal} / 9</td>
                </tr>
                {hyperactivityImpulsivityData.map((item, index) => (
                  <tr key={index} className="bg-primary/5 border-b border-primary/10">
                    <td className="p-3 text-sm text-center font-bold text-primary">{item.criterion}</td>
                    <td className="p-3 text-xs">{item.symptom}</td>
                    <td className="p-3 text-center bg-white">
                      {item.adulthood === true
                        ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                        : item.adulthood === false
                          ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                          : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                    <td className="p-3 text-center bg-white">
                      {item.childhood === true
                        ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                        : item.childhood === false
                          ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                          : <span className="text-gray-400 text-xs">—</span>}
                    </td>
                  </tr>
                ))}
                <tr className="bg-primary/15 font-bold text-primary">
                  <td className="p-3" colSpan="2">Total criteria: Hyperactivity / Impulsivity</td>
                  <td className="p-3 text-center">{hyperactivityTotals.adulthoodTotal} / 9</td>
                  <td className="p-3 text-center">{hyperactivityTotals.childhoodTotal} / 9</td>
                </tr>
              </tbody>
            </table>

            {/* Criterion D: Domains of Functional Impairment summary
                Domain name column spans the same 60% as Criterion + Symptom above,
                so the adulthood (20%) and childhood (20%) columns are vertically aligned. */}
            {domainImpairmentData.length > 0 && (
              <table className="w-full text-left shadow-lg rounded-lg overflow-hidden" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '60%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-3">Domain of Functional Impairment</th>
                    <th className="p-3 text-center">Impaired during adulthood</th>
                    <th className="p-3 text-center">Impaired during childhood</th>
                  </tr>
                </thead>
                <tbody>
                  {domainImpairmentData.map((item, index) => (
                    <tr key={index} className="bg-primary/5 border-b border-primary/10">
                      <td className="p-3 text-sm font-semibold text-primary">{item.domain}</td>
                      <td className="p-3 text-center bg-white">
                        {item.adulthood === true
                          ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                          : item.adulthood === false
                            ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                            : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="p-3 text-center bg-white">
                        {item.childhood === true
                          ? <CheckCircleIcon className="w-5 h-5 text-green-500 mx-auto" />
                          : item.childhood === false
                            ? <XCircleIcon className="w-5 h-5 text-red-500 mx-auto" />
                            : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-primary/15 font-bold text-primary">
                    <td className="p-3">Total domains impaired</td>
                    <td className="p-3 text-center">{domainImpairmentData.filter(d => d.adulthood).length} / {domainImpairmentData.length}</td>
                    <td className="p-3 text-center">{domainImpairmentData.filter(d => d.childhood).length} / {domainImpairmentData.length}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>

          <div className="w-1/2 bg-gray-50 rounded-2xl border border-gray-200 p-6 shadow-md">
            {/* Re-prefill notification banner — shown when upstream symptom or
                domain edits caused one or more criteria (A-D) to be re-derived
                to a different value than what the clinician last saved.
                Uses max-height + opacity transition so it slides in smoothly
                rather than snapping into place (same pattern as the risk banner
                in 11_RSK.js and the collateral warning below). */}
            {(() => {
              const show = reprefilledCriteriaLabels.length > 0;
              const msg = show ? buildReprefillMessage(reprefilledCriteriaLabels) : null;
              return (
                <div className={`overflow-hidden transition-all duration-500 ease-in-out ${show ? 'max-h-96 opacity-100 mb-4' : 'max-h-0 opacity-0'}`}>
                  <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                      <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                        {msg?.summary} <span className="font-bold">{msg?.cta}</span>
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
            {questionPanels.map((panel, panelIndex) => (
              <React.Fragment key={panelIndex}>
              <div
                className={`${panelIndex > 0 ? 'border-t border-gray-200' : ''} py-2`}
              >
                <div className="flex items-center mb-2 gap-2">
                  <h4 className="font-semibold text-lg text-left">{panel.title}</h4>
                  {panel.tooltip && (
                    <InfoTooltip
                      content={panel.tooltip}
                      label={`Information about ${panel.title}`}
                      maxWidth="300px"
                      className="flex-shrink-0"
                    />
                  )}
                </div>

                {/* Criterion E prefill reason banner — explains to the clinician
                    why Criterion E was pre-selected or left blank. Placed above
                    the question so the clinician sees the context first. */}
                {panelIndex === 4 && criterionEPrefill.reason && (
                  <div className="mt-1 mb-2">
                    <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
                      <div className="flex items-start">
                        <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                        <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                          {criterionEPrefill.reason}
                          {criterionEPrefill.cta && <>{' '}<span className="font-bold">{criterionEPrefill.cta}</span></>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {panel.questions.map((question, questionIndex) => {
                  // First determine if this question should be shown based on its showWhen condition
                  if (question.showWhen && !question.showWhen(selections)) {
                    return null; // Don't render this question at all if showWhen returns false
                  }

                  return (
                    <div key={questionIndex} className={`flex mb-2 text-left`}>
                      <div className={`${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-1/4 mt-4' : 'w-11/12'} text-sm pr-2 flex flex-col gap-1`}>
                        <div className="flex items-start">
                          <span>{question.name}</span>
                          {question.tooltip && (
                            <InfoTooltip
                              content={question.tooltip}
                              label="Question information"
                              className="flex-shrink-0 ml-1"
                            />
                          )}
                        </div>
                        {/* Show instruction as subtitle if it exists */}
                        {question.subtitle && (
                          <span className="text-xs text-gray-600 italic">
                            {question.subtitle}
                          </span>
                        )}
                      </div>

                      {/* Question inputs section */}
                      <div className={`flex flex-col ${(question.radio_options || question.checklist_options || question.isTextInput) ? 'w-full mt-4' : ''}`}>
                        {question.isTextInput ? (
                          <textarea
                            ref={(el) => { if (el) { el.style.height = '0px'; el.style.height = el.scrollHeight + 'px'; } }}
                            value={selections[panelIndex][questionIndex] || ''}
                            onChange={(e) => handleSelection(panelIndex, questionIndex, e.target.value)}
                            className={`w-full p-2 border border-gray-300 rounded-md ${!canEdit ? 'bg-gray-100 cursor-not-allowed opacity-75' : ''}`}
                            style={{ overflow: 'hidden', resize: 'none' }}
                            rows={3}
                            placeholder={canEdit ? "Please specify..." : "Notes can only be added by the assigned clinician"}
                            onInput={(e) => { e.target.style.height = '0px'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            disabled={!canEdit}
                          />
                        ) : question.radio_options ? (
                          question.radio_options.map((option, optionIndex) => {
                            const optionValue = typeof option === 'object' ? option.value : option;
                            const optionTooltip = typeof option === 'object' ? option.tooltip : null;

                            return (
                              <div
                                key={optionIndex}
                                className={`flex items-center justify-between px-2 py-1 rounded-lg transition-all duration-200 
                                  ${!canEdit ? 'opacity-75' : ''}
                                  ${selections[panelIndex][questionIndex] === optionValue
                                    ? 'bg-green-200 border-gray-600 border-2'
                                    : 'border border-transparent'
                                  }`}
                              >
                                <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                  <input
                                    type="radio"
                                    name={question.id}
                                    value={optionValue}
                                    checked={selections[panelIndex][questionIndex] === optionValue}
                                    onChange={() => handleSelection(panelIndex, questionIndex, optionValue)}
                                    className="mr-2 accent-green-600"
                                    disabled={!canEdit}
                                  />
                                  <div className="flex items-center">
                                    <span>{optionValue}</span>
                                    {optionTooltip && (
                                      <InfoTooltip
                                        content={optionTooltip}
                                        label={`Information about option ${optionValue}`}
                                        side="right"
                                        className="flex-shrink-0 ml-1"
                                      />
                                    )}
                                  </div>
                                </label>
                                {canEdit && selections[panelIndex][questionIndex] === optionValue && (
                                  <button
                                    onClick={() => handleSelection(panelIndex, questionIndex, null)}
                                    className="text-gray-500 hover:text-gray-700 text-sm ml-2"
                                    aria-label="Clear selection"
                                  >
                                    <XIcon className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            );
                          })
                        ) : question.checklist_options ? (
                          <div className="space-y-1">
                            {question.checklist_options.map((option, optionIndex) => {
                              const isNoneAvailable = option.value === 'None available';
                              const currentSelections = Array.isArray(selections[panelIndex][questionIndex])
                                ? selections[panelIndex][questionIndex]
                                : [];
                              const isSelected = currentSelections.includes(option.value);
                              const isDisabledByNone = !isNoneAvailable && currentSelections.includes('None available');
                              const isDisabledByOthers = isNoneAvailable && currentSelections.length > 0 && !isSelected;

                              return (
                                <div
                                  key={optionIndex}
                                  className={`
                                  flex items-center p-1 rounded-lg transition-all duration-200
                                  ${isSelected ? 'bg-green-200 border-gray-600 border-2' : 'border border-transparent'}
                                  ${(isDisabledByNone || isDisabledByOthers || !canEdit) ? 'opacity-50' : ''}
                                `}
                                >
                                  <label className={`flex items-center text-sm grow ${canEdit ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                                    <input
                                      type="checkbox"
                                      checked={isSelected}
                                      disabled={isDisabledByNone || isDisabledByOthers || !canEdit}
                                      onChange={() => {
                                        let newSelections = [...currentSelections];

                                        if (isNoneAvailable) {
                                          // If "None available" is clicked
                                          newSelections = isSelected ? [] : ['None available'];
                                        } else {
                                          // For other options
                                          if (isSelected) {
                                            // Remove if already selected
                                            newSelections = newSelections.filter(s => s !== option.value);
                                          } else {
                                            // Add new selection and remove "None available" if present
                                            newSelections = newSelections
                                              .filter(s => s !== 'None available')
                                              .concat(option.value);
                                          }
                                        }

                                        handleSelection(panelIndex, questionIndex, newSelections);
                                      }}
                                      className="mr-2 accent-green-600 h-3 w-3"
                                    />
                                    <span>{option.value}</span>
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleSelection(panelIndex, questionIndex, true)}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex ${selections[panelIndex][questionIndex] === true ? 'bg-green-200 border-gray-600 border-2' : 'bg-white border-gray-300'} transition-all duration-300 ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''}`}
                              disabled={!canEdit}
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">Yes</div>
                              </div>
                            </button>
                            <button
                              onClick={() => handleSelection(panelIndex, questionIndex, false)}
                              className={`grow shrink basis-0 h-9 px-8 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex ${selections[panelIndex][questionIndex] === false ? 'bg-red-200 border-gray-600 border-2' : 'bg-white'} transition-all duration-300 ${!canEdit ? 'opacity-75 cursor-not-allowed' : ''}`}
                              disabled={!canEdit}
                            >
                              <div className="px-0.5 justify-center items-center flex">
                                <div className="text-slate-700 text-sm font-semibold leading-tight">No</div>
                              </div>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Deferred-DF banner — when DF comes after RDD, let the clinician
                  know their formulation will be captured on the next page. */}
              {panelIndex === 4 && wf.isDFDeferred && selections[4]?.[0] !== null && (
                <div className={`overflow-hidden transition-all duration-500 ease-in-out
                  ${selections[4][0] !== null ? 'max-h-96 opacity-100 mt-1 mb-4' : 'max-h-0 opacity-0'}`}>
                  <div className="rounded-2xl p-4 text-left" style={{ backgroundColor: 'rgba(67, 33, 62, 0.08)' }}>
                    <div className="flex items-start">
                      <InformationCircleIcon className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#43213e' }} />
                      <p className="ml-3 text-sm" style={{ color: '#43213e' }}>
                        {selections[4][0] === true
                          ? "You will be able to record your Diagnostic Formulation on the next page."
                          : "You will be able to record your Diagnostic Formulation on the next page to record the conditions you are considering."}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              </React.Fragment>
            ))}

             {/* No Collateral Warning */}
            <div className={`overflow-hidden transition-all duration-400 ease-in-out ${
              selections[5][6]?.includes('None available') ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}>
              <div className="bg-red-50 rounded-2xl p-4 text-left risk-banner">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 mt-1">
                              <ExclamationIcon className="h-5 w-5 text-red-600" aria-hidden="true" />
                            </div>
                  <div className="ml-3">
                  <h3 className="text-md font-medium text-red-800">For Consideration</h3>
                  <div className="mt-2 text-sm text-red-700">
                      <p className="font-medium">
                        In the absence of corroborating information, a diagnosis of Attention Deficit Hyperactivity Disorder in adults should be made with caution.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">              
      {/* Forward button */}
      {canEdit && (
      <button
        onClick={() => handleFinishSSI()}
        className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md transition-all duration-300 ${isFormValid() ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        disabled={!isFormValid()}
      >
        <span className="font-bold">{isSaving ? "Saving Progress..." : (wf.isDFDeferred ? "Go to Diagnostic Formulation" : "Finish Semi-Structured Interview")}</span>
        {wf.isDFDeferred
          ? <ChevronLeftIcon className="w-5 h-5 ml-2 rotate-180" aria-hidden="true" />
          : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
      </button>
      )}
      </div>
      </div>

      <ModuleCompletionDialog
        open={isDialogOpen && !isGenerating}
        onOpenChange={setIsDialogOpen}
        title={wf.isDFDeferred ? 'Recommended Diagnosis Complete' : 'Semi-Structured Interview Complete'}
        description={wf.isDFDeferred
          ? <>Review your ADHD recommendations before proceeding to the <span className="font-semibold">Diagnostic Formulation</span>.</>
          : 'Review your diagnostic recommendations before generating the report.'}
        actions={
          <>
            <CancelButton onClick={() => setIsDialogOpen(false)} />
            {!wf.isDFDeferred && (
              <GenerateReportButton onClick={handleGenerateReport} />
            )}
            {wf.isDFDeferred && (
              <PrimaryActionButton
                label="Go to Diagnostic Formulation"
                onClick={handleProceedToDF}
              />
            )}
          </>
        }
      >
        <SummarySection title="Your Recommendations:">
          {constructDiagnosisSummary()}
        </SummarySection>
        <DialogMessage>
          {wf.isDFDeferred
            ? <>You will now proceed to the <span className="font-semibold not-italic">Diagnostic Formulation</span>, where you can record the conditions you are considering and your formulation notes. The SSI report will be generated in the background.</>
            : 'Please review your selections. Are you happy to proceed with generating the report?'}
        </DialogMessage>
      </ModuleCompletionDialog>

      <GenerateReportProgressDialog
        reportStage="Semi-Structured Interview"
        patientId={patientId}
        clinicianId={clinicianId}
        handleNextPageButtonClick={() => handleNextPageButtonClick(
          wf.isDFDeferred
            ? `/patient/${patientId}/proforma/semi-structured-interview/diagnostic-formulation`
            : `/patient/${patientId}/documents`
        )}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />
    </Layout>
  );
}